import { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  feedStockTransactionApi,
  FeedStockTransaction,
  CreateFeedStockTransactionDto,
  UpdateFeedStockTransactionDto,
  FeedStockTransactionFilterDto,
  TransactionType,
  TransactionStatus
} from '../api/feedStockTransaction';

export const useFeedStockTransactions = () => {
  const [transactions, setTransactions] = useState<FeedStockTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get all transactions with optional filters
  const getTransactions = useCallback(async (filters?: FeedStockTransactionFilterDto) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await feedStockTransactionApi.getAll(filters);
      setTransactions(data);
      return data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch transactions';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get transactions for a specific feed stock
  const getTransactionsByFeedStock = useCallback(async (
    feedStockId: string, 
    filters?: Omit<FeedStockTransactionFilterDto, 'feedStockId'>
  ) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await feedStockTransactionApi.getByFeedStock(feedStockId, filters);
      return data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch transactions';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get transaction by ID
  const getTransactionById = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await feedStockTransactionApi.getById(id);
      return data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch transaction';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new transaction
  const createTransaction = useCallback(async (data: CreateFeedStockTransactionDto) => {
    setLoading(true);
    setError(null);
    
    try {
      const newTransaction = await feedStockTransactionApi.create(data);
      setTransactions(prev => [newTransaction, ...prev]);
      
      // Show success message based on transaction type
      const typeMessages = {
        [TransactionType.RESTOCK]: 'Stock replenishment recorded successfully',
        [TransactionType.USAGE]: 'Feed usage recorded successfully',
        [TransactionType.ADJUSTMENT]: 'Stock adjustment recorded successfully',
        [TransactionType.COUNT]: 'Stock count recorded successfully',
        [TransactionType.WASTE]: 'Waste transaction recorded successfully'
      };
      
      toast.success(typeMessages[data.type] || 'Transaction created successfully');
      return newTransaction;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create transaction';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update transaction
  const updateTransaction = useCallback(async (id: string, data: UpdateFeedStockTransactionDto) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedTransaction = await feedStockTransactionApi.update(id, data);
      setTransactions(prev => 
        prev.map(transaction => 
          transaction.id === id ? updatedTransaction : transaction
        )
      );
      toast.success('Transaction updated successfully');
      return updatedTransaction;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update transaction';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Cancel transaction
  const cancelTransaction = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const cancelledTransaction = await feedStockTransactionApi.cancel(id);
      setTransactions(prev => 
        prev.map(transaction => 
          transaction.id === id ? cancelledTransaction : transaction
        )
      );
      toast.success('Transaction cancelled successfully');
      return cancelledTransaction;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to cancel transaction';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Helper function to get transaction type display name
  const getTransactionTypeDisplay = useCallback((type: TransactionType) => {
    const typeDisplayNames = {
      [TransactionType.RESTOCK]: 'Restock',
      [TransactionType.USAGE]: 'Usage',
      [TransactionType.ADJUSTMENT]: 'Adjustment',
      [TransactionType.COUNT]: 'Count',
      [TransactionType.WASTE]: 'Waste'
    };
    return typeDisplayNames[type] || type;
  }, []);

  // Helper function to get transaction status display name
  const getTransactionStatusDisplay = useCallback((status: TransactionStatus) => {
    const statusDisplayNames = {
      [TransactionStatus.PENDING]: 'Pending',
      [TransactionStatus.COMPLETED]: 'Completed',
      [TransactionStatus.CANCELLED]: 'Cancelled',
      [TransactionStatus.FAILED]: 'Failed'
    };
    return statusDisplayNames[status] || status;
  }, []);

  // Helper function to get transaction type color
  const getTransactionTypeColor = useCallback((type: TransactionType) => {
    const typeColors = {
      [TransactionType.RESTOCK]: 'bg-green-100 text-green-800',
      [TransactionType.USAGE]: 'bg-blue-100 text-blue-800',
      [TransactionType.ADJUSTMENT]: 'bg-yellow-100 text-yellow-800',
      [TransactionType.COUNT]: 'bg-purple-100 text-purple-800',
      [TransactionType.WASTE]: 'bg-red-100 text-red-800'
    };
    return typeColors[type] || 'bg-gray-100 text-gray-800';
  }, []);

  // Helper function to get transaction status color
  const getTransactionStatusColor = useCallback((status: TransactionStatus) => {
    const statusColors = {
      [TransactionStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
      [TransactionStatus.COMPLETED]: 'bg-green-100 text-green-800',
      [TransactionStatus.CANCELLED]: 'bg-gray-100 text-gray-800',
      [TransactionStatus.FAILED]: 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  }, []);

  return {
    transactions,
    loading,
    error,
    getTransactions,
    getTransactionsByFeedStock,
    getTransactionById,
    createTransaction,
    updateTransaction,
    cancelTransaction,
    getTransactionTypeDisplay,
    getTransactionStatusDisplay,
    getTransactionTypeColor,
    getTransactionStatusColor
  };
}; 